import { FC, ReactNode, useContext } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { AccountContext, EDITOR_ROLES, RoleType } from "auth/accountProvider";
import Card from "components/card/Card";
import { ArrowRightIcon } from "./icons";

interface ActionDef {
  /** Internal route (react-router) — mutually exclusive with `href`. */
  to?: string;
  /** External URL, opened in a new tab. */
  href?: string;
  title: string;
  desc: string;
  go: string;
  /** Roles required to see the card. Omitted means every signed-in user. */
  roles?: RoleType[];
}

const QuickActions: FC = () => {
  const { t } = useTranslation();
  const { hasRole } = useContext(AccountContext);

  const allActions: ActionDef[] = [
    {
      to: "/cpi-reports",
      title: t("dashboard.actionReports"),
      desc: t("dashboard.actionReportsDesc"),
      go: t("dashboard.openReports"),
    },
    {
      to: "/data",
      title: t("dashboard.actionUpload"),
      desc: t("dashboard.actionUploadDesc"),
      go: t("dashboard.goToData"),
      // /data is editor-gated; a viewer following this card would bounce to
      // /no-access, so drop the card rather than offer a dead end.
      roles: EDITOR_ROLES,
    },
    {
      to: "/emission-factors",
      title: t("dashboard.actionEmissionFactors"),
      desc: t("dashboard.actionEmissionFactorsDesc"),
      go: t("dashboard.openEmissionFactors"),
    },
  ];

  const actions = allActions.filter((a) => !a.roles || hasRole(a.roles));

  const inner = (a: ActionDef): ReactNode => (
    <Card customClass="overview-action__card">
      <div className="overview-action__title">{a.title}</div>
      <div className="overview-action__desc">{a.desc}</div>
      <span className="overview-action__go">
        {a.go} <ArrowRightIcon />
      </span>
    </Card>
  );

  return (
    <div className="overview-actions">
      {actions.map((a) =>
        a.href ? (
          <a
            key={a.href}
            href={a.href}
            target="_blank"
            rel="noopener noreferrer"
            className="overview-action"
          >
            {inner(a)}
          </a>
        ) : (
          <Link key={a.to} to={a.to ?? "/"} className="overview-action">
            {inner(a)}
          </Link>
        )
      )}
    </div>
  );
};

export default QuickActions;
